import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  Search, MapPin, ShoppingBag, ChevronRight, Minus, Plus, X, Clock3,
  ShieldCheck, UserRound, LayoutDashboard, PackageCheck, Check, ArrowLeft,
  Pencil, Trash2, Bike, Store, LogOut, CircleCheck, CircleAlert, Users, Info,
} from "lucide-react";
import "@/App.css";
import { OtpLogin } from "@/components/OtpLogin";
import { loadRazorpay } from "@/lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Send the httpOnly session cookie automatically on every request.
axios.defaults.withCredentials = true;
const hero = "https://static.prod-images.emergentagent.com/jobs/2736816e-b616-4edb-8e5b-b916f35b6861/images/bb5aedfd5d6217f48ca8502f86ffc70af5cfcc508757c026a05ecae5e6c457ea.jpeg";
const aromaLogo = "https://static.prod-images.emergentagent.com/jobs/2736816e-b616-4edb-8e5b-b916f35b6861/images/5d6cef309a953544645142e06f050524661bc1e426ed61ea136ae7d39f3128f3.jpeg";
const money = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const lineTotal = (i) => Math.round(i.p.price * i.qty * (i.p.weight / 1000));
const categories = [
  { name: "All", icon: "✦" }, { name: "Chicken", icon: "◈" },
  { name: "Mutton", icon: "◇" }, { name: "Fish", icon: "≈" }, { name: "Seafood", icon: "◌" },
];

/* -------------------- Shared primitives -------------------- */
function Logo() {
  return (
    <div className="logo" data-testid="aroma-logo">
      <span className="brand-mark">aroma</span>
    </div>
  );
}

function Toast({ id, msg, type = "ok", onClose }) {
  useEffect(() => { const t = setTimeout(() => onClose(id), 3200); return () => clearTimeout(t); }, [id, onClose]);
  return (
    <div className={`toast ${type}`} data-testid="toast">
      {type === "err" ? <CircleAlert size={18}/> : type === "info" ? <Info size={18}/> : <CircleCheck size={18}/>}<span>{msg}</span>
    </div>
  );
}

function Modal({ onClose, children, size = "sm", testid }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`sheet sheet-${size}`} data-testid={testid} onClick={(e)=>e.stopPropagation()}>
        <button className="icon-btn close" data-testid="modal-close" onClick={onClose}><X/></button>
        {children}
      </div>
    </div>
  );
}

/* -------------------- Customer: product card, cart, auth, checkout -------------------- */
function ProductCard({ p, onAdd }) {
  return (
    <article className="product-card" data-testid={`product-card-${p.id}`}>
      <div className="product-image">
        <img src={p.image} alt={p.name} />
        <span className="product-tag">{p.tag}</span>
      </div>
      <div className="product-info">
        <div className="product-heading">
          <h3 data-testid={`product-name-${p.id}`}>{p.name}</h3>
          <span className="fresh-dot">● fresh</span>
        </div>
        <p>{p.description}</p>
        <div className="product-bottom">
          <div><strong>{money(p.price)}</strong><span> / kg</span></div>
          <button className="add-btn" data-testid={`add-product-${p.id}`} onClick={() => onAdd(p)}>
            <Plus size={17}/> Add
          </button>
        </div>
      </div>
    </article>
  );
}

function AuthModal({ onClose, onLogin }) {
  return (
    <Modal onClose={onClose} testid="auth-modal">
      <div className="auth-modal-body">
        <div className="auth-kicker">WELCOME TO AROMA</div>
        <h2>Fresh starts here.</h2>
        <p>Sign in with your mobile number to save addresses and follow every order.</p>
        <OtpLogin role="customer" askName onLogin={onLogin} testPrefix="auth" />
        <small className="auth-foot">By continuing, you agree to Aroma’s terms and privacy policy.</small>
      </div>
    </Modal>
  );
}

function Cart({ items, onClose, onRemove, onChange, onCheckout }) {
  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  return (
    <aside className="cart-drawer" data-testid="cart-drawer">
      <div className="cart-head">
        <div><span className="eyebrow">YOUR ORDER</span><h2>Fresh in your cart</h2></div>
        <button className="icon-btn" data-testid="cart-close-button" onClick={onClose}><X/></button>
      </div>
      {!items.length ? (
        <div className="empty-cart"><ShoppingBag size={42}/><h3>Your cart is empty</h3><p>Add something fresh for dinner.</p></div>
      ) : (
        <>
          <div className="cart-lines">
            {items.map(({ p, qty }) => (
              <div className="cart-line" key={p.id}>
                <img src={p.image} alt="" />
                <div className="line-main">
                  <strong>{p.name}</strong><span>{money(p.price)} / kg</span>
                  <div className="stepper">
                    <button data-testid={`cart-minus-${p.id}`} onClick={() => onChange(p, -1)}><Minus size={14}/></button>
                    <span data-testid={`cart-quantity-${p.id}`}>{qty}</span>
                    <button data-testid={`cart-plus-${p.id}`} onClick={() => onChange(p, 1)}><Plus size={14}/></button>
                  </div>
                </div>
                <button className="remove" data-testid={`cart-remove-${p.id}`} onClick={() => onRemove(p.id)}><X size={16}/></button>
              </div>
            ))}
          </div>
          <div className="cart-summary">
            <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
            <div><span>Delivery</span><strong>{subtotal >= 499 ? "FREE" : money(39)}</strong></div>
            <div className="total"><span>Total</span><strong>{money(subtotal + (subtotal >= 499 ? 0 : 39))}</strong></div>
            <button className="primary full" data-testid="cart-checkout-button" onClick={onCheckout}>
              Continue to checkout <ChevronRight size={18}/>
            </button>
            <p className="secure"><ShieldCheck size={15}/> Secure checkout · COD accepted</p>
          </div>
        </>
      )}
    </aside>
  );
}

function Checkout({ items, onBack, onSuccess, user }) {
  const [address, setAddress] = useState("H.No. 8-2-124, Wyra Road, Khammam");
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [config, setConfig] = useState({});
  const [pending, setPending] = useState(null);
  const [payError, setPayError] = useState("");
  useEffect(() => { axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {}); }, []);
  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const total = subtotal + (subtotal >= 499 ? 0 : 39);
  const openRazorpay = async (order) => {
    await loadRazorpay();
    const rz = new window.Razorpay({
      key: order.razorpay.key_id, amount: order.razorpay.amount, currency: order.razorpay.currency, order_id: order.razorpay.order_id,
      name: "Aroma", description: `Order #${order.id.replace("ord_", "")}`,
      prefill: { name: user?.name, contact: user?.phone }, theme: { color: "#ef6b4f" },
      handler: async (resp) => {
        try { const v = await axios.post(`${API}/payments/razorpay/verify`, { order_id: order.id, ...resp }); setDone(v.data); }
        catch (e) { setPayError(e.response?.data?.detail || "Payment verification failed"); }
      },
      modal: { ondismiss: () => setPayError("Payment cancelled. You can retry whenever you're ready.") },
    });
    rz.on("payment.failed", (resp) => setPayError(resp.error?.description || "Payment failed. Please try again."));
    rz.open();
  };
  const place = async () => {
    setPayError("");
    try {
      setPlacing(true);
      if (payment === "razorpay" && pending) { await openRazorpay(pending); return; }
      const r = await axios.post(`${API}/orders`, {
        customer_name: user?.name || "Aroma customer",
        phone: user?.phone || "9999999999",
        address: { label: "Home", line: address, city: "Khammam", pincode: "507001" },
        items: items.map((i) => ({ product_id: i.p.id, quantity: i.qty, customization: "Cleaned" })),
        payment_method: payment,
      });
      if (payment === "razorpay") { setPending(r.data); await openRazorpay(r.data); }
      else setDone(r.data);
    } catch (e) { setPayError(e.response?.data?.detail || e.message || "Could not place order"); }
    finally { setPlacing(false); }
  };
  if (done) return (
    <div className="checkout-page" data-testid="order-success">
      <div className="success-icon"><Check/></div>
      <span className="eyebrow">ORDER CONFIRMED</span>
      <h1>We’re on it.</h1>
      <p>Your fresh order <strong>#{done.id.replace("ord_","")}</strong> is confirmed and the shop is getting started.</p>
      <p data-testid="order-payment-status">{done.payment_status === "paid" ? "Payment received online. Thank you!" : "Please pay cash when your order arrives."}</p>
      <div className="eta-box"><Clock3/><div><span>Estimated arrival</span><strong>{done.eta}</strong></div></div>
      <button className="primary" data-testid="back-to-shop-button" onClick={onSuccess}>Back to shop</button>
    </div>
  );
  return (
    <div className="checkout-page" data-testid="checkout-page">
      <button className="back-btn" data-testid="checkout-back-button" onClick={onBack}><ArrowLeft size={18}/> Back to cart</button>
      <div className="checkout-grid">
        <section>
          <span className="eyebrow">CHECKOUT</span>
          <h1>Almost at the good part.</h1>
          <div className="checkout-section">
            <h3>Delivery address</h3>
            <div className="address-card active">
              <MapPin/>
              <div><strong>Home</strong><p>{address}</p><small>Khammam · 507001</small></div>
              <Check className="address-check"/>
            </div>
            <button className="text-btn" data-testid="edit-address-button"
              onClick={() => setAddress(address.includes("Wyra") ? "Door 12, Mamillagudem, Khammam" : "H.No. 8-2-124, Wyra Road, Khammam")}>
              + Use another address
            </button>
          </div>
          <div className="checkout-section">
            <h3>Payment method</h3>
            <button className={`payment-card ${payment === "cod" ? "selected" : ""}`} data-testid="payment-cod-option" onClick={() => setPayment("cod")}>
              <span className="payment-icon">₹</span>
              <span><strong>Cash on delivery</strong><small>Pay when your order arrives</small></span>
              {payment === "cod" && <Check/>}
            </button>
            <button className={`payment-card ${payment === "razorpay" ? "selected" : ""}`} data-testid="payment-razorpay-option"
              disabled={!config.razorpay_enabled} onClick={() => setPayment("razorpay")}>
              <span className="payment-icon upi">UPI</span>
              <span><strong>Pay online</strong><small>{config.razorpay_enabled ? "UPI, cards, netbanking & wallets via Razorpay" : "Coming soon: online payments are being set up"}</small></span>
              {payment === "razorpay" && <Check/>}
            </button>
          </div>
        </section>
        <aside className="checkout-summary">
          <h3>Order summary</h3>
          {items.map((i) => (
            <div className="summary-line" key={i.p.id}>
              <span>{i.p.name} × {i.qty}</span>
              <strong>{money(lineTotal(i))}</strong>
            </div>
          ))}
          <hr/>
          <div className="summary-line"><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
          <div className="summary-line"><span>Delivery fee</span><strong>{subtotal >= 499 ? "Free" : money(39)}</strong></div>
          <div className="summary-total"><span>To pay</span><strong>{money(total)}</strong></div>
          <button className="primary full" data-testid="place-order-button" onClick={place} disabled={placing}>
            {placing ? "Please wait…" : payment === "razorpay" ? `${pending ? "Retry payment" : "Pay"} · ${money(total)}` : `Place order · ${money(total)}`}
          </button>
          {payError && <div className="error" data-testid="checkout-error">{payError}</div>}
          <small><ShieldCheck size={14}/> Safe, secure & hygienic delivery</small>
        </aside>
      </div>
    </div>
  );
}

/* -------------------- Staff auth (admin / shop / delivery) -------------------- */
function StaffAuthScreen({ role, onBack, onLogin, allowRegister = true }) {
  const [mode, setMode] = useState("login");
  const [method, setMethod] = useState(role === "admin" ? "password" : "otp");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", business_name: "", area: "Khammam" });
  const [busy, setBusy] = useState(false); const [error, setError] = useState(""); const [notice, setNotice] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const roleLabel = role === "admin" ? "Admin" : role === "shop" ? "Partner shop" : "Delivery partner";
  const submit = async () => {
    setError(""); setNotice("");
    try {
      setBusy(true);
      if (mode === "login") {
        const r = await axios.post(`${API}/auth/staff/login`, { email: form.email, password: form.password });
        if (r.data.user.role !== role) throw new Error(`Please sign in on the ${r.data.user.role} portal`);
        onLogin(r.data.user);
      } else {
        await axios.post(`${API}/auth/staff/register`, { ...form, role });
        setNotice("Application submitted! Our team will approve your account shortly.");
        setMode("login");
      }
    } catch (e) { setError(e.response?.data?.detail || e.message || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return (
    <div className="staff-auth" data-testid={`staff-auth-${role}`}>
      <header className="admin-head">
        <Logo/>
        <button className="back-btn" data-testid="staff-auth-back" onClick={onBack}><ArrowLeft size={18}/> Customer view</button>
      </header>
      <div className="staff-auth-grid">
        <div className="staff-auth-hero">
          <span className="eyebrow light">{role === "shop" ? "FOR PARTNER SHOPS" : role === "delivery" ? "FOR DELIVERY PARTNERS" : "ADMIN PORTAL"}</span>
          <h1>{role === "shop" ? "Grow with Aroma." : role === "delivery" ? "Ride with Aroma." : "Operations central."}</h1>
          <p>{role === "shop" ? "List your meat shop on Khammam’s premium delivery platform. Approvals typically within 24 hours."
            : role === "delivery" ? "Flexible deliveries, straight payouts. ₹35 per completed delivery to start."
            : "Manage products, orders, partners, and city operations in one place."}</p>
          <ul className="staff-perks">
            {(role === "shop" ? ["10% platform commission", "Daily payouts", "Live order queue"]
              : role === "delivery" ? ["₹35 per delivery", "Pick orders you want", "Weekly settlement"]
              : ["Full catalogue control", "Approve partners", "Live revenue"]).map((x) => <li key={x}><Check size={14}/> {x}</li>)}
          </ul>
        </div>
        <div className="staff-auth-form">
          <div className="auth-kicker">{roleLabel.toUpperCase()} · {mode === "login" ? "SIGN IN" : "APPLY"}</div>
          <h2>{mode === "login" ? "Welcome back" : "Join Aroma"}</h2>
          {mode === "register" && (
            <>
              <label>Full name</label>
              <input data-testid="staff-name" value={form.name} onChange={set("name")} placeholder="Your name" />
              <label>Mobile</label>
              <input data-testid="staff-phone" value={form.phone} onChange={set("phone")} placeholder="98765 43210" />
              {role === "shop" && (<><label>Shop name</label>
                <input data-testid="staff-business" value={form.business_name} onChange={set("business_name")} placeholder="Aroma Meats, Wyra Road" /></>)}
              <label>Area in Khammam</label>
              <input data-testid="staff-area" value={form.area} onChange={set("area")} placeholder="Wyra Road / Mamillagudem / …" />
            </>
          )}
          {mode === "login" && role !== "admin" && (
            <div className="method-tabs" data-testid="staff-login-method">
              <button className={method === "otp" ? "active" : ""} data-testid="staff-method-otp" onClick={() => { setMethod("otp"); setError(""); }}>Mobile OTP</button>
              <button className={method === "password" ? "active" : ""} data-testid="staff-method-password" onClick={() => { setMethod("password"); setError(""); }}>Email & password</button>
            </div>
          )}
          {mode === "login" && method === "otp" ? (
            <OtpLogin role={role} onLogin={onLogin} testPrefix={`staff-otp-${role}`} />
          ) : (
            <>
              <label>Email</label>
              <input data-testid={`staff-email-${role}`} value={form.email} onChange={set("email")} placeholder={role === "admin" ? "admin@aroma.in" : "you@example.com"} />
              <label>Password</label>
              <input data-testid={`staff-password-${role}`} type="password" value={form.password} onChange={set("password")} placeholder="Minimum 6 characters" />
              <button className="primary full" data-testid={`staff-submit-${role}`} onClick={submit} disabled={busy || !form.email || !form.password}>
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Submit application"}
              </button>
            </>
          )}
          {error && <div className="error" data-testid="staff-error">{error}</div>}
          {notice && <div className="demo-note" data-testid="staff-notice">{notice}</div>}
          {allowRegister && (
            <small className="auth-foot">
              {mode === "login" ? "New here?" : "Already have an account?"}{" "}
              <button className="link-btn" data-testid="staff-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}>
                {mode === "login" ? "Apply as a partner" : "Sign in instead"}
              </button>
            </small>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Admin dashboard -------------------- */
function ProductForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    name: "", category: "Chicken", description: "", price: 299, stock: 10, image: "", tag: "Fresh today", weight: 500, customizations: [], ...initial,
  });
  const set = (k) => (e) => setF({ ...f, [k]: ["price", "stock", "weight"].includes(k) ? Number(e.target.value) : e.target.value });
  const submit = () => {
    if (!f.name || !f.category || f.price <= 0) return;
    onSave({ ...f, customizations: typeof f.customizations === "string" ? f.customizations.split(",").map((x) => x.trim()).filter(Boolean) : (f.customizations || []) });
  };
  return (
    <div className="product-form" data-testid="product-form">
      <h2>{initial?.id ? "Edit product" : "Add a product"}</h2>
      <div className="field-grid">
        <div><label>Name</label><input data-testid="pf-name" value={f.name} onChange={set("name")} placeholder="Chicken Curry Cut"/></div>
        <div><label>Category</label>
          <select data-testid="pf-category" value={f.category} onChange={set("category")}>
            {["Chicken","Mutton","Fish","Seafood"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label>Price (₹/kg)</label><input data-testid="pf-price" type="number" value={f.price} onChange={set("price")} /></div>
        <div><label>Stock (kg)</label><input data-testid="pf-stock" type="number" value={f.stock} onChange={set("stock")} /></div>
        <div><label>Pack weight (g)</label><input data-testid="pf-weight" type="number" value={f.weight} onChange={set("weight")} /></div>
        <div><label>Tag</label><input data-testid="pf-tag" value={f.tag} onChange={set("tag")} placeholder="Bestseller" /></div>
      </div>
      <label>Description</label>
      <textarea data-testid="pf-description" rows={2} value={f.description} onChange={set("description")} placeholder="Fresh, bone-in pieces…" />
      <label>Image URL <em>optional</em></label>
      <input data-testid="pf-image" value={f.image} onChange={set("image")} placeholder="https://…" />
      <label>Customizations <em>comma separated</em></label>
      <input data-testid="pf-customizations" value={Array.isArray(f.customizations) ? f.customizations.join(", ") : f.customizations} onChange={(e)=>setF({...f, customizations: e.target.value})} placeholder="Curry cut, Boneless, Cleaned"/>
      <div className="form-actions">
        <button className="text-btn" data-testid="pf-cancel" onClick={onCancel}>Cancel</button>
        <button className="primary" data-testid="pf-save" onClick={submit}>{initial?.id ? "Save changes" : "Create product"}</button>
      </div>
    </div>
  );
}

function AdminDashboard({ user, onLogout, onBack, toast }) {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const [s, o, p, pr] = await Promise.all([
        axios.get(`${API}/admin/summary`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/partners`),
      ]);
      setSummary(s.data); setOrders(o.data); setProducts(p.data); setPartners(pr.data);
    } catch (e) { toast(e.response?.data?.detail || "Could not refresh dashboard", "err"); }
  }, [toast]);
  useEffect(() => { refresh(); }, [refresh]);
  const saveProduct = async (payload) => {
    try {
      if (payload.id) await axios.patch(`${API}/admin/products/${payload.id}`, payload);
      else await axios.post(`${API}/admin/products`, payload);
      toast(payload.id ? "Product updated" : "Product added");
      setEditing(null); refresh();
    } catch (e) { toast(e.response?.data?.detail || "Could not save product", "err"); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Remove this product from the catalogue?")) return;
    try { await axios.delete(`${API}/admin/products/${id}`); toast("Product removed"); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not delete", "err"); }
  };
  const updateOrder = async (id, status) => {
    try { await axios.patch(`${API}/admin/orders/${id}`, { status }); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not update order", "err"); }
  };
  const updatePartner = async (id, status) => {
    try { await axios.patch(`${API}/admin/partners/${id}`, { status }); toast(`Partner ${status}`); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not update partner", "err"); }
  };

  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-head">
        <Logo/>
        <div className="admin-nav" data-testid="admin-nav">
          {["overview","products","orders","partners"].map((t) => (
            <button key={t} className={tab === t ? "active" : ""} data-testid={`admin-tab-${t}`} onClick={()=>setTab(t)}>{t[0].toUpperCase()+t.slice(1)}</button>
          ))}
        </div>
        <div className="admin-top-actions">
          <span className="admin-whoami">{user?.email}</span>
          <button className="back-btn" data-testid="admin-exit-button" onClick={onBack}><ArrowLeft size={16}/> Customer view</button>
          <button className="back-btn" data-testid="admin-logout" onClick={onLogout}><LogOut size={16}/> Sign out</button>
        </div>
      </header>
      <main>
        {tab === "overview" && (
          <>
            <div className="admin-title">
              <div><span className="eyebrow">OPERATIONS · KHAMMAM</span><h1>Good morning, admin.</h1><p>Here’s what’s moving through Aroma today.</p></div>
            </div>
            <div className="stat-grid">
              {[
                ["Total orders", summary?.total_orders ?? "—", "All time"],
                ["Gross revenue", summary ? money(summary.revenue) : "—", "Before commissions"],
                ["Platform commission", summary ? money(summary.platform_commission) : "—", "10% of gross"],
                ["Pending orders", summary?.pending_orders ?? "—", "Need attention"],
                ["Active customers", summary?.active_customers ?? "—", "Unique buyers"],
                ["Approved shops", summary?.active_shops ?? "—", "Partner shops"],
                ["Pending partners", summary?.pending_partners ?? "—", "Awaiting approval"],
                ["Completed deliveries", summary?.completed_deliveries ?? "—", "Delivered"],
              ].map((s, i) => (
                <div className="stat-card" data-testid={`admin-stat-${i}`} key={s[0]}>
                  <span>{s[0]}</span><strong>{s[1]}</strong><small>{s[2]}</small>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "products" && (
          <section>
            <div className="section-head">
              <div><span className="eyebrow">CATALOGUE</span><h2>Products ({products.length})</h2></div>
              <button className="primary" data-testid="admin-add-product-button" onClick={() => setEditing({})}>
                <Plus size={16}/> Add product
              </button>
            </div>
            <div className="product-table" data-testid="admin-product-table">
              {products.map((p) => (
                <div className="product-row" data-testid={`admin-product-${p.id}`} key={p.id}>
                  <img src={p.image} alt="" />
                  <div><strong>{p.name}</strong><small>{p.category} · {p.weight}g pack</small></div>
                  <span className="price-tag">{money(p.price)}/kg</span>
                  <span className={`stock-tag ${p.stock < 5 ? "low" : ""}`}>{p.stock} kg left</span>
                  <div className="row-actions">
                    <button className="icon-btn edit" data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)}><Pencil size={15}/></button>
                    <button className="icon-btn danger" data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)}><Trash2 size={15}/></button>
                  </div>
                </div>
              ))}
              {!products.length && <div className="empty-table"><PackageCheck size={30}/><p>No products yet — add your first one.</p></div>}
            </div>
          </section>
        )}

        {tab === "orders" && (
          <section>
            <div className="section-head">
              <div><span className="eyebrow">LIVE QUEUE</span><h2>Orders ({orders.length})</h2></div>
            </div>
            <div className="orders-table">
              {orders.length ? orders.map((o) => (
                <div className="order-row admin-order-row" data-testid={`admin-order-${o.id}`} key={o.id}>
                  <span className="order-id">#{o.id.replace("ord_","")}</span>
                  <div><strong>{o.customer_name}</strong><small>{o.items?.length || 0} items · {o.payment_method?.toUpperCase?.()}</small></div>
                  <span className="order-status">{o.status_label}</span>
                  <strong>{money(o.total)}</strong>
                  <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => updateOrder(o.id, e.target.value)}>
                    {["placed","accepted","preparing","ready","out_for_delivery","delivered","cancelled"].map((s) => <option key={s} value={s}>{s.replaceAll("_"," ")}</option>)}
                  </select>
                </div>
              )) : <div className="empty-table"><PackageCheck size={30}/><p>No orders yet.</p></div>}
            </div>
          </section>
        )}

        {tab === "partners" && (
          <section>
            <div className="section-head">
              <div><span className="eyebrow">PARTNER NETWORK</span><h2>Shops & delivery ({partners.length})</h2></div>
            </div>
            <div className="partner-table">
              {partners.length ? partners.map((pt) => (
                <div className="partner-row" data-testid={`admin-partner-${pt.id}`} key={pt.id}>
                  <span className="partner-role">{pt.role === "shop" ? <Store size={16}/> : <Bike size={16}/>} {pt.role}</span>
                  <div><strong>{pt.name}{pt.business_name ? ` · ${pt.business_name}` : ""}</strong><small className="partner-meta">{pt.email} · {pt.phone} · {pt.area}</small></div>
                  <span className={`status-pill status-${pt.status}`}>{pt.status}</span>
                  <div className="row-actions">
                    {pt.status !== "approved" && <button className="chip-btn ok" data-testid={`admin-approve-${pt.id}`} onClick={()=>updatePartner(pt.id,"approved")}>Approve</button>}
                    {pt.status !== "suspended" && <button className="chip-btn danger" data-testid={`admin-suspend-${pt.id}`} onClick={()=>updatePartner(pt.id,"suspended")}>Suspend</button>}
                  </div>
                </div>
              )) : <div className="empty-table"><Users size={30}/><p>No partner applications yet.</p></div>}
            </div>
          </section>
        )}
      </main>
      {editing && (
        <Modal onClose={() => setEditing(null)} size="md" testid="product-dialog">
          <ProductForm initial={editing} onSave={saveProduct} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

/* -------------------- Shop dashboard -------------------- */
function ShopDashboard({ user, onLogout, onBack, toast }) {
  const [orders, setOrders] = useState([]); const [earn, setEarn] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const [o, e] = await Promise.all([
        axios.get(`${API}/shop/orders`),
        axios.get(`${API}/shop/earnings`),
      ]);
      setOrders(o.data); setEarn(e.data);
    } catch (e) { toast(e.response?.data?.detail || "Could not load orders", "err"); }
  }, [toast]);
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);
  const accept = async (id) => {
    try { await axios.post(`${API}/shop/orders/${id}/accept`, {}); toast("Order accepted"); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not accept", "err"); }
  };
  const setStatus = async (id, status) => {
    try { await axios.patch(`${API}/shop/orders/${id}`, { status }); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not update", "err"); }
  };
  const unassigned = orders.filter((o) => !o.shop_id && o.status === "placed");
  const mine = orders.filter((o) => o.shop_id === user.id);
  return (
    <div className="admin-page" data-testid="shop-page">
      <header className="admin-head">
        <Logo/>
        <div className="admin-top-actions">
          <span className="admin-whoami"><Store size={14}/> {user.business_name || user.name}</span>
          <button className="back-btn" data-testid="shop-exit-button" onClick={onBack}><ArrowLeft size={16}/> Customer view</button>
          <button className="back-btn" data-testid="shop-logout" onClick={onLogout}><LogOut size={16}/> Sign out</button>
        </div>
      </header>
      <main>
        <div className="admin-title">
          <div><span className="eyebrow">PARTNER SHOP · KHAMMAM</span><h1>Hello, {user.name}.</h1><p>New orders from Khammam customers — pick what you can prepare.</p></div>
        </div>
        <div className="stat-grid">
          {[
            ["Open orders", unassigned.length, "Grab the next one"],
            ["My orders", mine.length, "In your queue"],
            ["Delivered", earn?.delivered ?? "—", "Lifetime"],
            ["Earnings (payout)", earn ? money(earn.payout) : "—", "After 10% commission"],
          ].map((s, i) => (
            <div className="stat-card" data-testid={`shop-stat-${i}`} key={s[0]}>
              <span>{s[0]}</span><strong>{s[1]}</strong><small>{s[2]}</small>
            </div>
          ))}
        </div>

        <section className="admin-table-section">
          <div className="section-head"><div><span className="eyebrow">LIVE QUEUE</span><h2>Open orders</h2></div></div>
          <div className="orders-table">
            {unassigned.length ? unassigned.map((o) => (
              <div className="order-row shop-row" data-testid={`shop-open-${o.id}`} key={o.id}>
                <span className="order-id">#{o.id.replace("ord_","")}</span>
                <div><strong>{o.customer_name}</strong><small>{o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</small></div>
                <span>{o.address?.line}</span>
                <strong>{money(o.total)}</strong>
                <button className="primary" data-testid={`shop-accept-${o.id}`} onClick={() => accept(o.id)}>Accept</button>
              </div>
            )) : <div className="empty-table"><PackageCheck size={30}/><p>No open orders — new ones will drop here live.</p></div>}
          </div>
        </section>

        <section className="admin-table-section">
          <div className="section-head"><div><span className="eyebrow">IN YOUR KITCHEN</span><h2>My orders</h2></div></div>
          <div className="orders-table">
            {mine.length ? mine.map((o) => (
              <div className="order-row shop-row" data-testid={`shop-mine-${o.id}`} key={o.id}>
                <span className="order-id">#{o.id.replace("ord_","")}</span>
                <div><strong>{o.customer_name}</strong><small>{o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</small></div>
                <span className="order-status">{o.status_label}</span>
                <strong>{money(o.total)}</strong>
                <select data-testid={`shop-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}>
                  {["accepted","preparing","ready","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )) : <div className="empty-table"><PackageCheck size={30}/><p>Accept an order to start preparing.</p></div>}
          </div>
        </section>
      </main>
    </div>
  );
}

/* -------------------- Delivery dashboard -------------------- */
function DeliveryDashboard({ user, onLogout, onBack, toast }) {
  const [orders, setOrders] = useState([]); const [earn, setEarn] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const [o, e] = await Promise.all([
        axios.get(`${API}/delivery/orders`),
        axios.get(`${API}/delivery/earnings`),
      ]);
      setOrders(o.data); setEarn(e.data);
    } catch (e) { toast(e.response?.data?.detail || "Could not load", "err"); }
  }, [toast]);
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);
  const accept = async (id) => {
    try { await axios.post(`${API}/delivery/orders/${id}/accept`, {}); toast("Delivery accepted"); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not accept", "err"); }
  };
  const markDelivered = async (id) => {
    try { await axios.patch(`${API}/delivery/orders/${id}`, { status: "delivered" }); toast("Marked delivered"); refresh(); }
    catch (e) { toast(e.response?.data?.detail || "Could not update", "err"); }
  };
  const open = orders.filter((o) => !o.delivery_id && o.status === "ready");
  const mine = orders.filter((o) => o.delivery_id === user.id);
  const inProgress = mine.filter((o) => o.status !== "delivered");
  return (
    <div className="admin-page" data-testid="delivery-page">
      <header className="admin-head">
        <Logo/>
        <div className="admin-top-actions">
          <span className="admin-whoami"><Bike size={14}/> {user.name}</span>
          <button className="back-btn" data-testid="delivery-exit-button" onClick={onBack}><ArrowLeft size={16}/> Customer view</button>
          <button className="back-btn" data-testid="delivery-logout" onClick={onLogout}><LogOut size={16}/> Sign out</button>
        </div>
      </header>
      <main>
        <div className="admin-title">
          <div><span className="eyebrow">DELIVERY PARTNER · KHAMMAM</span><h1>Ready to ride, {user.name}?</h1><p>Pick up ready orders near you and complete deliveries.</p></div>
        </div>
        <div className="stat-grid">
          {[
            ["Available", open.length, "Nearby pickups"],
            ["Mine", inProgress.length, "In progress"],
            ["Delivered", earn?.delivered ?? "—", "Completed"],
            ["Earnings", earn ? money(earn.earnings) : "—", "₹35 / delivery"],
          ].map((s, i) => (
            <div className="stat-card" data-testid={`delivery-stat-${i}`} key={s[0]}>
              <span>{s[0]}</span><strong>{s[1]}</strong><small>{s[2]}</small>
            </div>
          ))}
        </div>

        <section className="admin-table-section">
          <div className="section-head"><div><span className="eyebrow">READY FOR PICKUP</span><h2>Available deliveries</h2></div></div>
          <div className="orders-table">
            {open.length ? open.map((o) => (
              <div className="order-row shop-row" data-testid={`delivery-open-${o.id}`} key={o.id}>
                <span className="order-id">#{o.id.replace("ord_","")}</span>
                <div><strong>{o.customer_name}</strong><small>{o.phone}</small></div>
                <span>{o.address?.line}</span>
                <strong>{money(o.total)}</strong>
                <button className="primary" data-testid={`delivery-accept-${o.id}`} onClick={() => accept(o.id)}>Accept</button>
              </div>
            )) : <div className="empty-table"><Bike size={30}/><p>No ready orders — check back in a bit.</p></div>}
          </div>
        </section>

        <section className="admin-table-section">
          <div className="section-head"><div><span className="eyebrow">ON MY ROUTE</span><h2>My deliveries</h2></div></div>
          <div className="orders-table">
            {mine.length ? mine.map((o) => (
              <div className="order-row shop-row" data-testid={`delivery-mine-${o.id}`} key={o.id}>
                <span className="order-id">#{o.id.replace("ord_","")}</span>
                <div><strong>{o.customer_name}</strong><small>{o.phone} · {o.address?.line}</small></div>
                <span className="order-status">{o.status_label}</span>
                <strong>{money(o.total)}</strong>
                {o.status !== "delivered" && <button className="primary" data-testid={`delivery-complete-${o.id}`} onClick={() => markDelivered(o.id)}>Mark delivered</button>}
              </div>
            )) : <div className="empty-table"><Bike size={30}/><p>Accept a ready order to start your route.</p></div>}
          </div>
        </section>
      </main>
    </div>
  );
}

/* -------------------- Portal picker -------------------- */
function PortalPicker({ onPick, onClose }) {
  return (
    <Modal onClose={onClose} testid="portal-picker">
      <div className="auth-kicker">AROMA PORTALS</div>
      <h2>Who’s signing in?</h2>
      <p>Pick the dashboard that matches your Aroma role.</p>
      <div className="portal-grid">
        <button className="portal-card" data-testid="pick-admin" onClick={() => onPick("admin")}>
          <LayoutDashboard/><strong>Admin</strong><small>Catalogue, orders, partners</small>
        </button>
        <button className="portal-card" data-testid="pick-shop" onClick={() => onPick("shop")}>
          <Store/><strong>Partner shop</strong><small>Accept and prep orders</small>
        </button>
        <button className="portal-card" data-testid="pick-delivery" onClick={() => onPick("delivery")}>
          <Bike/><strong>Delivery</strong><small>Pick, ride, earn</small>
        </button>
      </div>
    </Modal>
  );
}

/* -------------------- Customer app -------------------- */
function CustomerApp() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [auth, setAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [portalPick, setPortalPick] = useState(false);
  const [resume, setResume] = useState(false);
  const [menu, setMenu] = useState(false);
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* ignore */ }
    setUser(null); setMenu(false); toast("Signed out", "info");
  };
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((msg, type = "ok") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, type }]);
  }, []);
  const removeToast = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  useEffect(() => { axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/auth/me`).then((r) => { if (r.data.role === "customer") setUser(r.data); }).catch(() => {}); }, []);

  const filtered = useMemo(() =>
    products.filter((p) => (category === "All" || p.category === category) && (!search || p.name.toLowerCase().includes(search.toLowerCase()))),
    [products, category, search]
  );
  const add = (p) => { setCart((c) => { const f = c.find((i) => i.p.id === p.id); return f ? c.map((i) => i.p.id === p.id ? { ...i, qty: i.qty + 1 } : i) : [...c, { p, qty: 1 }]; }); setShowCart(true); };

  if (checkout) return <Checkout items={cart} user={user} onBack={() => setCheckout(false)} onSuccess={() => { setCart([]); setCheckout(false); }} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <Logo/>
        <div className="location" data-testid="delivery-location">
          <MapPin size={16}/>
          <span><small>DELIVERING TO</small><strong>Khammam, Telangana</strong></span>
          <ChevronRight size={15}/>
        </div>
        <div className="header-actions">
          <button className="admin-link" data-testid="open-portal-button" onClick={() => setPortalPick(true)}>
            <LayoutDashboard size={16}/> Portals
          </button>
          <div className="account-wrap">
            <button className="account-btn" data-testid="open-auth-button" onClick={() => (user ? setMenu(!menu) : setAuth(true))}>
              <UserRound size={19}/><span>{user ? user.name.split(" ")[0] : "Sign in"}</span>
            </button>
            {user && menu && (
              <div className="account-menu" data-testid="account-menu">
                <small data-testid="account-phone">+91 {user.phone}</small>
                <button data-testid="customer-logout" onClick={logout}><LogOut size={15}/> Sign out</button>
              </div>
            )}
          </div>
          <button className="cart-btn" data-testid="open-cart-button" onClick={() => setShowCart(true)}>
            <ShoppingBag size={20}/><span>Cart</span>
            {cart.length > 0 && <b data-testid="cart-count">{cart.reduce((s, i) => s + i.qty, 0)}</b>}
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <span className="eyebrow light">KHAMMAM’S FRESH PICK</span>
            <h1>Cut fresh.<br/><i>Delivered fresh.</i></h1>
            <p>Quality meat from trusted local shops, delivered to your doorstep in about 30 minutes.</p>
            <div className="hero-perks">
              <span><Clock3/>30–45 min</span>
              <span><ShieldCheck/>Hygienic handling</span>
            </div>
          </div>
          <div className="hero-art"><img src={hero} alt="Freshly prepared chicken" /></div>
        </section>

        <section className="shop-content">
          <div className="shop-intro">
            <div><span className="eyebrow">SHOP FRESH TODAY</span><h2>Good food starts here.</h2></div>
            <div className="search-wrap">
              <Search size={18}/>
              <input data-testid="product-search-input" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search chicken, mutton, fish…" />
            </div>
          </div>
          <nav className="category-nav" data-testid="category-navigation">
            {categories.map((c) => (
              <button key={c.name} className={category === c.name ? "active" : ""} data-testid={`category-${c.name.toLowerCase()}`} onClick={() => setCategory(c.name)}>
                <span>{c.icon}</span>{c.name}
              </button>
            ))}
          </nav>
          <div className="delivery-banner">
            <div>
              <span className="live-dot"></span>
              <strong>Serving Khammam today</strong>
              <span>Wyra Road · Mamillagudem · Srinagar Colony</span>
            </div>
            <button className="text-btn" data-testid="check-coverage-button">Check coverage <ChevronRight size={15}/></button>
          </div>
          <div className="product-grid" data-testid="product-grid">
            {filtered.map((p) => <ProductCard p={p} onAdd={add} key={p.id} />)}
          </div>
          {!filtered.length && <div className="no-results">No fresh cuts match that search.</div>}
        </section>
      </main>

      <footer>
        <div><Logo/><p>Cut fresh. Delivered fresh.<br/>Made for Khammam.</p></div>
        <div className="footer-nav">
          <span className="footer-nav-title">Portals</span>
          <button className="footer-link" data-testid="footer-admin-link" onClick={() => navigate("/admin")}><LayoutDashboard size={14}/> Admin</button>
          <button className="footer-link" data-testid="footer-shop-link" onClick={() => navigate("/shop")}><Store size={14}/> Partner shop</button>
          <button className="footer-link" data-testid="footer-delivery-link" onClick={() => navigate("/delivery")}><Bike size={14}/> Delivery</button>
        </div>
        <div className="footer-links"><span>Freshness promise</span><span>Delivery areas</span><span>Help & support</span></div>
        <span className="copyright">© 2026 Aroma</span>
      </footer>

      {showCart && (
        <>
          <div className="drawer-overlay" onClick={() => setShowCart(false)}></div>
          <Cart items={cart}
            onClose={() => setShowCart(false)}
            onRemove={(id) => setCart((c) => c.filter((i) => i.p.id !== id))}
            onChange={(product, d) => setCart((c) => c.map((i) => i.p.id === product.id ? { ...i, qty: Math.max(1, i.qty + d) } : i))}
            onCheckout={() => { setShowCart(false); if (user) setCheckout(true); else { setResume(true); setAuth(true); toast("Sign in to place your order", "info"); } }} />
        </>
      )}
      {auth && <AuthModal onClose={() => { setAuth(false); setResume(false); }} onLogin={(u) => { setUser(u); setAuth(false); toast(`Welcome ${u.name || "to Aroma"}`); if (resume) { setResume(false); setCheckout(true); } }} />}
      {portalPick && <PortalPicker onClose={() => setPortalPick(false)} onPick={(r) => { setPortalPick(false); navigate(`/${r}`); }} />}
      <div className="toast-stack">{toasts.map((t) => <Toast key={t.id} {...t} onClose={removeToast} />)}</div>
    </div>
  );
}

/* -------------------- Staff portal (per-role via /admin, /shop, /delivery) -------------------- */
function StaffPortal({ role }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem("aroma_staff") || "null"); } catch { return null; } });
  const [storedRole, setStoredRole] = useState(() => localStorage.getItem("aroma_staff_role") || "");
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((msg, type = "ok") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, type }]);
  }, []);
  const removeToast = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const onLogin = (u) => {
    setUser(u); setStoredRole(role);
    // Only non-sensitive user info is stored client-side; the auth token lives in an httpOnly cookie.
    localStorage.setItem("aroma_staff", JSON.stringify(u));
    localStorage.setItem("aroma_staff_role", role);
    toast(`Welcome, ${u.name || u.email}`);
  };
  const onLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* ignore */ }
    setUser(null); setStoredRole("");
    localStorage.removeItem("aroma_staff"); localStorage.removeItem("aroma_staff_role");
    navigate("/");
  };
  const onBack = () => navigate("/");

  const [checked, setChecked] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then((r) => { if (r.data.role !== role) throw new Error("role"); setUser(r.data); setStoredRole(role); })
      .catch(() => { setUser(null); setStoredRole(""); localStorage.removeItem("aroma_staff"); localStorage.removeItem("aroma_staff_role"); })
      .finally(() => setChecked(true));
  }, [role]);
  const authed = user && storedRole === role;
  const toastStack = <div className="toast-stack">{toasts.map((t) => <Toast key={t.id} {...t} onClose={removeToast} />)}</div>;

  if (!checked) return <div className="staff-auth" data-testid="staff-session-loading" />;
  if (!authed) {
    return (
      <>
        <StaffAuthScreen role={role} onBack={onBack} onLogin={onLogin} allowRegister={role !== "admin"} />
        {toastStack}
      </>
    );
  }
  const common = { user, onLogout, onBack, toast };
  const Dash = role === "admin" ? AdminDashboard : role === "shop" ? ShopDashboard : DeliveryDashboard;
  return <><Dash {...common} />{toastStack}</>;
}

/* -------------------- App root (routed) -------------------- */
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<CustomerApp/>} />
      <Route path="/admin" element={<StaffPortal role="admin"/>} />
      <Route path="/shop" element={<StaffPortal role="shop"/>} />
      <Route path="/delivery" element={<StaffPortal role="delivery"/>} />
      <Route path="*" element={<Navigate to="/" replace/>} />
    </Routes>
  );
}
